import { Module } from 'vuex';
import { SelectionPanelOption } from '@/components/SelectionPanel/SelectionPanel.models';
import i18n from '../../i18n';
import { RootState } from '..';
import { Floor, HouseConfiguration } from '@/utils/api';
import { Step } from '@/components/ProcessBar/ProcessBar.models';
import axios from 'axios';
import loadImgSrc from '@/utils/helpers/loadImgSrc';
import getOutsideLayers from '@/utils/helpers/getOutsideLayers';
import { ResponsiveImageMedia } from '@/components/utils/ResponsiveImage/ResponsiveImage.models';
import { layeringEngineMediaDimenstions } from '@/components/ConfContainer/ConfContainer.models';
import { Layer } from '@/components/LayeringEngine/LayeringEngine.models';

const SET_VIDEO_READY = 'SET_VIDEO_READY';
const SET_VIDEO_ENDED = 'SET_VIDEO_ENDED';
const SET_VIDEO_BLOB_URL = 'SET_VIDEO_BLOB_URL';
const RESET_STATE = 'RESET_STATE';

export interface HouseSizeState {
  videoBlobUrl?: string;
  videoReady: boolean;
  videoEnded: boolean;
}

const houseSizeInitialState = (): HouseSizeState => ({
  videoBlobUrl: undefined,
  videoReady: false,
  videoEnded: false
});

export const houseSizeModule: Module<HouseSizeState, RootState> = {
  namespaced: true,
  state: houseSizeInitialState(),
  getters: {
    floor(_state, getters, rootState): Floor | undefined {
      return {
        ID: '0',
        name: i18n.t('views.houseSize.headline').toString(),
        description: i18n.t('views.houseSize.description').toString(),
        image: rootState.configuration.sizes[0]?.image,
        zones: [
          {
            ID: '0',
            name: i18n.t('views.houseSize.headline').toString(),
            shortName: i18n.t('views.houseSize.headline').toString(),
            description: i18n.t('views.houseSize.description').toString(),
            modules: getters.houseSizeOptions,
            isDeactivated: false
          }
        ]
      };
    },
    // * maybe move into floor getter & change return type to Module
    houseSizeOptions(_state, _getters, rootState, rootGetters): SelectionPanelOption[] {
      return rootState.configuration.sizes.map(size => ({
        ID: size.ID,
        name: size.name,
        description: `${i18n.t('views.houseSize.options.area', size.area)} <br/>
        ${i18n.t('views.houseSize.options.rooms', size.rooms)} <br/>
        ${i18n.t('views.houseSize.options.persons', size.persons)} <br/>
        ${i18n.t('views.houseSize.options.price', { price: i18n.n(size.price, 'currency', 'de-DE') })}
        `,
        isSelected: size.ID === rootGetters['configuration/selectedSize']?.ID
      }));
    },
    stepCompleted(_state, _getters, rootState): boolean {
      const currentStep: Step | undefined = rootState.app.processRoutes.find(route => route.name === 'HouseSize');

      return !!currentStep?.completed;
    }
  },
  actions: {
    setVideoReady({ commit }, ready: boolean) {
      commit(SET_VIDEO_READY, ready);
    },
    setVideoEnded({ commit }, ended: boolean) {
      commit(SET_VIDEO_ENDED, ended);
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    },
    async preloadVideo({ commit, rootState }) {
      if (rootState.configuration.houseConfiguration?.video) {
        // Make request to get blob of video and set it to the HTML5 Video El
        const response = await axios.get(rootState.configuration.houseConfiguration.video, {
          responseType: 'blob',
          // Don't check status of call on the axios side to avoid axios' interceptor
          validateStatus: () => true
        });

        // In case of success set videoBlobUrl and resolve this promise
        if ([200, 206].includes(response.status)) {
          const videoBlobUrl = URL.createObjectURL(response.data);

          commit(SET_VIDEO_BLOB_URL, videoBlobUrl);
        }
      }
    },
    async preloadImages({ rootState }) {
      const houseConfiguration: HouseConfiguration | undefined = rootState.configuration.houseConfiguration;

      if (houseConfiguration) {
        await Promise.allSettled(
          getOutsideLayers(houseConfiguration, layeringEngineMediaDimenstions).map((layer: Layer) =>
            loadImgSrc(layer.media[rootState.app.imageMQ as keyof ResponsiveImageMedia])
          )
        );
      }

      return;
    }
  },
  mutations: {
    [SET_VIDEO_READY](state, ready: boolean) {
      state.videoReady = ready;
    },
    [SET_VIDEO_ENDED](state, ended: boolean) {
      state.videoEnded = ended;
    },
    [SET_VIDEO_BLOB_URL](state, url: string) {
      state.videoBlobUrl = url;
    },
    [RESET_STATE](state) {
      Object.assign(state, houseSizeInitialState());
    }
  }
};
